import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaArrowLeft, FaEye, FaEyeSlash, FaPlus } from 'react-icons/fa';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAlert } from '../../contexts/AlertContext';
import { useAbility } from '../../contexts/AbilityContext';
import { BackButton } from '../common/styles';
import { MyCV } from '../MyActivities';
import useCVStoreRepository from '../../store/cvStoreRepository';
import useCVRepositorySearch from '../../hooks/useCVRepositorySearch';
import useGridNavigation from '../../hooks/useGridNavigation';
import CVRepositoryFilters from './components/CVRepositoryFilters';
import CVGrid from './components/CVGrid';
import {
  Container,
  Header,
  HeaderLeft,
  Title,
  Content,
  ControlsContainer,
  MainContainer,
  HeaderButton,
} from './styles';

const CVRepository = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams(); // Get the CV ID from URL params
  const [showFilters, setShowFilters] = useState(true);
  const { 
    searchInputValue, 
    setSearchInputValue, 
    selectedPositions, 
    setSelectedPositions, 
    linkedFilter, 
    setLinkedFilter,
    selectedLanguage,
    setSelectedLanguage,
    selectedLevel,
    setSelectedLevel,
    levelComparator,
    setLevelComparator,
    selectedSkills,
    setSelectedSkills,
    clearSearchFilters 
  } = useCVRepositorySearch();
  const gridRef = useRef(null);
  
  // Get state and actions from Repository store
  const {
    cvs,
    isLoading,
    isLoadingMore,
    pageInfo,
    fetchCVsWithReset,
    fetchCVsLoadMore,
    setIsReturningFromDetails,
    resetState,
    resetFilters
  } = useCVStoreRepository();

  const loadMoreCallback = useCallback(() => {
    return fetchCVsLoadMore();
  }, [fetchCVsLoadMore]);

  // Callback to clean up filters when navigating away from CV Repository
  const handleNavigateAway = useCallback(() => {
    clearSearchFilters();
    resetFilters();
  }, [clearSearchFilters, resetFilters]);

  // Use the GridNavigation hook with repository routes
  const {
    showLoadingSpinner,
    handleCombinedGridScroll,
    handleItemClick,
    handleBackToItems,
    handleAddNewItem,
    handleCustomBack,
    shouldHideContent,
    loadMoreData,
    isNewItem,
    shouldLoadData,
    shouldForceReload,
    isReturningFromDetailPage,
    hasItems,
    navigateWithScrollState
  } = useGridNavigation({
    gridRef,
    items: cvs,
    columnCount: 3,
    rowHeight: 230,
    routes: {
      list: '/cv-repository',
      detail: (id) => `/cv-repository/${id}`,
      new: '/cv-repository/new',
      additionalRoutes: [
        { path: '/view', shouldLoadData: false }
      ]
    },
    onReturnFromDetail: () => {
      setIsReturningFromDetails(true);
    },
    loadMoreDataCallback: loadMoreCallback,
    pageInfo,
    isLoadingMore,
    onNavigateAway: handleNavigateAway
  });

  // First effect: Always reset the store on mount
  useEffect(() => {
    resetState();
  }, [resetState]); // Run whenever resetState changes or component mounts

  // ADR-109: deep link — /cv-repository?code=48291367 pre-fills the search with the CV code
  useEffect(() => {
    const code = new URLSearchParams(location.search).get('code');
    if (code && code.trim()) {
      setSearchInputValue(code.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Second effect: Load data when needed
  useEffect(() => {
    // Only run this effect when we're on the main repository view
    // using the centralized shouldLoadData function
    if (!shouldLoadData()) {
      return;
    }
    
    // CRITICAL FIX: Prevent reloading data if we already have items
    // This prevents infinite reload cycles when using cursor-based pagination
    if (hasItems && !isLoadingMore && !shouldForceReload()) {
      return;
    }

    // FIXED: Only force reload when items array is empty
    // The previous logic always forced reload during normal browsing
    const forceReload = shouldForceReload();
    
    // Fetch data with reset parameter based on whether we need to force reload
    fetchCVsWithReset(forceReload);
    
  // Removed cvs.length from dependencies to prevent data reload when pagination adds items
  }, [fetchCVsWithReset, shouldLoadData, shouldForceReload, isReturningFromDetailPage, hasItems, isLoadingMore]);

  // Render filters section
  const renderFilters = () => {
    return <CVRepositoryFilters 
      searchInputValue={searchInputValue}
      setSearchInputValue={setSearchInputValue}
      selectedPositions={selectedPositions}
      setSelectedPositions={setSelectedPositions}
      linkedFilter={linkedFilter}
      setLinkedFilter={setLinkedFilter}
      selectedLanguage={selectedLanguage}
      setSelectedLanguage={setSelectedLanguage}
      selectedLevel={selectedLevel}
      setSelectedLevel={setSelectedLevel}
      levelComparator={levelComparator}
      setLevelComparator={setLevelComparator}
      selectedSkills={selectedSkills}
      setSelectedSkills={setSelectedSkills}
    />;
  };

  // View handler - for CVs the user cannot manage
  const handleViewClick = (cv) => {
    navigateWithScrollState(cv, `/cv-repository/${cv.id}/view`);
  };

  // Render CVs grid
  const renderCVs = () => {
    return (
      <CVGrid
        cvs={cvs}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        pageInfo={pageInfo}
        handleItemClick={handleItemClick}
        handleGridScroll={handleCombinedGridScroll}
        showLoadingSpinner={showLoadingSpinner}
        shouldHideContent={shouldHideContent}
        loadMoreData={loadMoreData}
        gridRef={gridRef}
        onView={handleViewClick}
      />
    );
  };

  // If showing the Add CV form or CV detail, render the MyCV component
  if (id || isNewItem) {
    // Determine if we're in view mode (read-only)
    const readOnly = location.pathname.includes('/view');

    // Check if there's a custom returnTo path in the location state
    const handleBack = () => {
      if (location.state?.returnTo) {
        navigate(location.state.returnTo, { state: location.state.returnState });
      } else if (isNewItem) {
        handleCustomBack();
      } else {
        handleBackToItems();
      }
    };

    return (
      <MyCV
        cvId={id}
        onBack={handleBack}
        readonly={readOnly}
      />
    );
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/')}>
            <FaArrowLeft /> Back to Home
          </BackButton>
          <Title>CV Repository</Title>
        </HeaderLeft>
        <div style={{ display: 'flex', gap: '16px' }}>
          <HeaderButton onClick={handleAddNewItem}>
            <FaPlus /> Add CV
          </HeaderButton>
          <HeaderButton onClick={() => setShowFilters(!showFilters)}>
            {showFilters ? <FaEyeSlash /> : <FaEye />}
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </HeaderButton>
        </div>
      </Header>
      <Content>
        <ControlsContainer show={showFilters}>
          {renderFilters()}
        </ControlsContainer>
        <MainContainer show={showFilters}>
          <div style={{
            color: '#64748B',
            fontSize: '14px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            flexShrink: 0
          }}>
            <span style={{ fontWeight: 600 }}>
              {pageInfo?.totalCount || 0}
            </span>
            {pageInfo?.totalCount === 1 ? 'CV' : 'CVs'} found
          </div>

          {renderCVs()}
        </MainContainer>
      </Content>
    </Container>
  );
};

export default CVRepository; 